import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Send } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const PLAN_LABELS = { demo: "Demo", basic: "Basic", pro: "Pro" };

// The public contact endpoint already accepts these `interest` values and
// emails the result to ClientFlow + saves it under /admin/leads. We map
// "uncertain" → "annet" so it lands in the same inbox.
const INTEREST_OPTIONS = [
  { value: "basic", label: "Basic" },
  { value: "pro", label: "Pro" },
  { value: "annet", label: "Usikker, kontakt meg" },
];

/**
 * Plan-change request dialog (in-app version of the landing page contact
 * form, scoped to package changes). Submits to /api/public/contact so the
 * request appears in Super Admin → Forespørsler and triggers the same
 * email notification as a normal lead. NEVER changes the plan directly —
 * Super Admin still has to confirm and apply via the switcher footer.
 */
export default function PlanChangeDialog({
  open,
  onOpenChange,
  business,         // { name, plan }
  userEmail,
  userName,
  defaultInterest = "",   // e.g. "pro" — preselects the dropdown so the
                          // caller can pre-frame the request (Staff page
                          // opens this with "pro" preselected).
  source = "settings-billing",
}) {
  const [interest, setInterest] = useState(defaultInterest || "");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  // Reset the form every time the dialog opens so a re-open from a
  // different entry point picks up its own `defaultInterest` instead of
  // showing stale state from the previous submit.
  useEffect(() => {
    if (open) {
      setInterest(defaultInterest || "");
      setMessage("");
    }
  }, [open, defaultInterest]);

  const submit = async (e) => {
    e.preventDefault();
    if (!interest) {
      toast.error("Velg ønsket pakke");
      return;
    }
    const wantedLabel = INTEREST_OPTIONS.find((o) => o.value === interest)?.label || interest;
    const currentLabel = PLAN_LABELS[business?.plan] || business?.plan || "—";
    // Compose a structured message body so the lead is easy to triage
    // alongside landing-page inquiries (`[Pakkeendring]` prefix).
    const body = [
      "[Pakkeendring]",
      `Bedrift: ${business?.name || "—"}`,
      `Nåværende pakke: ${currentLabel}`,
      `Ønsket pakke: ${wantedLabel}`,
      "",
      message.trim() || "(ingen tilleggsmelding)",
    ].join("\n");
    setSending(true);
    try {
      await api.post("/public/contact", {
        name: userName || userEmail,
        email: userEmail,
        company: business?.name || "",
        message: body,
        interest,
        source,
      });
      toast.success("Forespørsel sendt");
      setMessage("");
      setInterest("");
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kunne ikke sende forespørsel");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-md"
        data-testid="plan-change-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-heading">Be om pakkeendring</DialogTitle>
          <DialogDescription>
            Pakkeendringer gjøres av ClientFlow. Vi tar kontakt så snart vi har sett forespørselen.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="rounded-xl border border-border bg-muted/40 p-3 text-sm">
            <div className="flex justify-between gap-2">
              <span className="text-muted-foreground">Bedrift</span>
              <span className="font-medium truncate">{business?.name || "—"}</span>
            </div>
            <div className="flex justify-between gap-2 mt-1">
              <span className="text-muted-foreground">Innlogget e-post</span>
              <span className="font-medium truncate">{userEmail || "—"}</span>
            </div>
            <div className="flex justify-between gap-2 mt-1">
              <span className="text-muted-foreground">Nåværende pakke</span>
              <span className="font-medium">
                {PLAN_LABELS[business?.plan] || business?.plan || "—"}
              </span>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="plan-change-wanted">Ønsket pakke</Label>
            <Select value={interest} onValueChange={setInterest}>
              <SelectTrigger id="plan-change-wanted" data-testid="plan-change-interest-trigger">
                <SelectValue placeholder="Velg pakke" />
              </SelectTrigger>
              <SelectContent>
                {INTEREST_OPTIONS.map((o) => (
                  <SelectItem
                    key={o.value}
                    value={o.value}
                    data-testid={`plan-change-interest-${o.value}`}
                  >
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="plan-change-message">Melding</Label>
            <Textarea
              id="plan-change-message"
              data-testid="plan-change-message"
              rows={4}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Fortell oss kort om behovet. F.eks. antall ansatte, tidsplan, spesielle ønsker."
              maxLength={1000}
            />
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="rounded-xl"
            >
              Avbryt
            </Button>
            <Button
              type="submit"
              disabled={sending}
              className="rounded-xl"
              data-testid="plan-change-submit"
            >
              <Send className="w-4 h-4 mr-1.5" />
              Send forespørsel
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
